import styled, { css, keyframes } from 'styled-components';

import { customScrollMinimal } from '~/components/custom-scroll/CustomScroll.styles';
import {
  DropdownGroup,
  DropdownItemButton,
} from '~/components/dropdown-menu/dropdown-group/DropdownGroup.styles';
import { DropdownMenu } from '~/components/dropdown-menu/DropdownMenu';
import { borderRadius } from '~/design-tokens/border-radius/borderRadius';

const gradientCycle = keyframes`
  0% { 
    background: conic-gradient(from 0deg, #9EB1FF, #3E63DD, #FF92AD, #E93D82, #29FFDC, #0EB39E, #9EB1FF);
  }
  12.5% { 
    background: conic-gradient(from 45deg, #9EB1FF, #3E63DD, #FF92AD, #E93D82, #29FFDC, #0EB39E, #9EB1FF);
  }
  25% { 
    background: conic-gradient(from 90deg, #FF92AD, #E93D82, #29FFDC, #0EB39E, #FBE577, #FFC53D, #FF92AD);
  }
  37.5% { 
    background: conic-gradient(from 135deg, #FF92AD, #E93D82, #29FFDC, #0EB39E, #FBE577, #FFC53D, #FF92AD);
  }
  50% { 
    background: conic-gradient(from 180deg, #29FFDC, #0EB39E, #FBE577, #FFC53D, #9EB1FF, #3E63DD, #29FFDC);
  }
  62.5% { 
    background: conic-gradient(from 225deg, #29FFDC, #0EB39E, #FBE577, #FFC53D, #9EB1FF, #3E63DD, #29FFDC);
  }
  75% { 
    background: conic-gradient(from 270deg, #FBE577, #FFC53D, #9EB1FF, #3E63DD, #FF92AD, #E93D82, #FBE577);
  }
  87.5% { 
    background: conic-gradient(from 315deg, #FBE577, #FFC53D, #9EB1FF, #3E63DD, #FF92AD, #E93D82, #FBE577);
  }
  100% { 
    background: conic-gradient(from 360deg, #9EB1FF, #3E63DD, #FF92AD);
  }
`;

const defaultGradient = css`
  background: linear-gradient(
    120deg,
    #3e63dd,
    #9eb1ff,
    #ffc53d,
    #ff92ad,
    #e93d82
  );
`;

const SHADOW_COLOR = '#6592fd';

const loadingTransition = keyframes`
  0% {
    --inset-size: -3px;
    --shadow-size: 5px;
    --scale: 1.01;
  }
  100% {
    --inset-size: -1px;
    --shadow-size: 2px;
    --scale: 1;
  }
`;

export const GradientWrapper = styled.div<{
  $isDefault?: boolean;
  $isLoading?: boolean;
  $isFinished?: boolean;
}>`
  border-radius: 100px;
  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 100px;
    z-index: -1;
  }

  ${({ $isDefault }) =>
    $isDefault &&
    css`
      &::before {
        inset: -1px;
        ${defaultGradient}
      }
      &::after {
        inset: 0;
      }
    `}

  ${({ $isFinished }) =>
    $isFinished &&
    css`
      box-shadow: 0px 0px 5px 1px ${SHADOW_COLOR};
      &::before {
        inset: -1px;
        ${defaultGradient}
      }
      &::after {
        inset: 0;
      }
    `}

  ${({ $isLoading }) =>
    $isLoading &&
    css`
      /* Initial loading phase (first 1000ms) */
      box-shadow: 0 0 15px 5px ${SHADOW_COLOR};

      &::before {
        inset: -3px;
        animation: ${gradientCycle} 1.5s linear infinite;
      }
      &::after {
        inset: 0;
      }

      /* After 1.5s, transition to loading phase */
      animation: ${loadingTransition} 1.5s ease-out forwards;

      &::before {
        inset: var(--inset-size, -3px);
      }

      box-shadow: 0 0 15px var(--shadow-size, 5px) ${SHADOW_COLOR};
      transform: scale(var(--scale, 1.01));
    `}
`;

export const StyledDropdown = styled(DropdownMenu)<{ $maxHeight?: number }>`
  width: 400px;
  min-width: 400px;
  max-width: 400px;
  margin-right: 0px;
  padding: 0px;
  ${borderRadius.r2};

  ${DropdownGroup} {
    gap: 0;
    max-height: ${({ $maxHeight }) => `${$maxHeight}px`};
    overflow-y: auto;
    ${customScrollMinimal}

    > div:last-child ${DropdownItemButton} {
      border-bottom: none;
    }
  }

  ${DropdownItemButton} {
    margin: 0;
    padding: 12px 8px;
    border-radius: 0;
    border-bottom: 1px solid var(--border-soft);
  }

  ${DropdownGroup} [data-highlighted]:first-child ${DropdownItemButton} {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }

  ${DropdownGroup} [data-highlighted]:last-child ${DropdownItemButton} {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
`;
